import type { ChangeEvent, FocusEvent, KeyboardEvent } from "react";
import { useRef, useState } from "react";

import { Card, Colors, InputGroup } from "@blueprintjs/core";
import styled from "@emotion/styled";

import { BP_NS } from "@/css/constants";

interface OptionSelectProps<T> {
  options: T[];
  onSelect: (option: T) => void;
  filterOption: (option: T, inputValue: string) => boolean;
  getOptionLabel: (option: T) => string;
  placeholder?: string;
  showResultsFromBlankQuery?: boolean;
}

export function OptionSelect<T>({
  options,
  onSelect,
  filterOption,
  getOptionLabel,
  placeholder = "Search...",
  showResultsFromBlankQuery = false,
}: OptionSelectProps<T>) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const filteredOptions = options.filter((option) =>
    filterOption(option, inputValue.trim()),
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setActiveIndex(null);
    setIsOpen(
      (value.trim().length > 0 || showResultsFromBlankQuery) &&
        filteredOptions.length > 0,
    );
  };

  const handleFocus = () => {
    if (
      (inputValue.trim().length > 0 || showResultsFromBlankQuery) &&
      filteredOptions.length > 0
    )
      setIsOpen(true);
  };

  const handleBlur = (e: FocusEvent) => {
    if (
      !e.relatedTarget ||
      !dropdownRef.current?.contains(e.relatedTarget as Node)
    ) {
      setTimeout(() => {
        setIsOpen(false);
      }, 200);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === "Enter" &&
      activeIndex != null &&
      filteredOptions[activeIndex]
    ) {
      e.preventDefault();
      handleSelect(filteredOptions[activeIndex]);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev === null || prev >= filteredOptions.length - 1 ? 0 : prev + 1,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev === null || prev <= 0 ? filteredOptions.length - 1 : prev - 1,
      );
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const handleSelect = (option: T) => {
    setInputValue("");
    setIsOpen(false);
    setActiveIndex(null);
    onSelect(option);
  };

  return (
    <StyledContainer>
      <InputGroup
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        value={inputValue}
        placeholder={placeholder}
        autoComplete="new-password"
        large
        fill
      />
      {isOpen && filteredOptions.length > 0 && (
        <StyledDropdown elevation={2} ref={dropdownRef} tabIndex={-1}>
          {filteredOptions.map((option, index) => (
            <SuggestionItem
              key={index}
              onClick={() => handleSelect(option)}
              active={activeIndex === index}
            >
              {getOptionLabel(option)}
            </SuggestionItem>
          ))}
        </StyledDropdown>
      )}
    </StyledContainer>
  );
}

const StyledContainer = styled("div")`
  position: relative;
  width: 100%;
`;

const StyledDropdown = styled(Card)`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  margin-top: 2px;
  padding: 0;
  border: none;
  max-height: 300px;
  overflow-y: auto;
  background: ${Colors.WHITE};
  box-shadow:
    0 0 0 1px rgba(16, 22, 26, 0.1),
    0 2px 4px rgba(16, 22, 26, 0.2),
    0 8px 24px rgba(16, 22, 26, 0.2);
  border-radius: 6px;

  &.${BP_NS}-card {
    padding: 8px;
  }
`;

const SuggestionItem = styled("div")<{ active: boolean }>`
  padding: 10px 8px;
  margin: 0;
  cursor: pointer;
  font-size: 16px;
  color: ${({ active }) => (active ? Colors.BLACK : Colors.DARK_GRAY1)};
  background: ${({ active }) => (active ? Colors.LIGHT_GRAY5 : "transparent")};
  transition: all 0.15s ease-in-out;
  line-height: 24px;

  &:first-of-type {
    background-color: ${({ active }) =>
      active ? Colors.LIGHT_GRAY5 : "transparent"};
    color: ${({ active }) => (active ? Colors.BLACK : Colors.DARK_GRAY1)};
  }

  &:hover {
    background: ${Colors.LIGHT_GRAY3};
    border-radius: 6px;
  }
`;
